import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Upload,
  Send,
  Zap,
  RefreshCw,
  ArrowLeft,
  FileSpreadsheet,
  Database,
  BarChart3,
  Play,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Download,
  ExternalLink,
  Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClayPipeline() {
  const [loading, setLoading] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [leads, setLeads] = useState([]);
  const [csvData, setCsvData] = useState("");
  const [clayWebhookUrl, setClayWebhookUrl] = useState("");
  const [batchSize, setBatchSize] = useState(100);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPipelineStatus();
    fetchLeads();
  }, []);

  const fetchPipelineStatus = async () => {
    try {
      const response = await axios.get(`${API}/clay/status`);
      setPipelineStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch pipeline status:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads?limit=50`);
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error("Failed to fetch leads:", error);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvData(e.target.result);
    };
    reader.readAsText(file);
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split("\n");
    if (lines.length < 2) return [];

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));
    const leads = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim().replace(/['"]/g, ""));
      const lead = {};

      headers.forEach((header, idx) => {
        // Map common CSV headers to our schema
        const mappings = {
          "company": "company_name",
          "company name": "company_name",
          "name": "company_name",
          "website": "website",
          "url": "website",
          "domain": "website",
          "linkedin": "linkedin_url",
          "linkedin url": "linkedin_url",
          "industry": "industry",
          "size": "company_size",
          "employees": "company_size",
          "company size": "company_size",
          "location": "location",
          "headquarters": "location",
          "hq": "location",
          "description": "description",
        };

        const key = mappings[header] || header;
        lead[key] = values[idx] || "";
      });

      if (lead.company_name || lead.website) {
        leads.push(lead);
      }
    }

    return leads;
  };

  const handleImport = async () => {
    if (!csvData.trim()) {
      toast.error("Please paste CSV data or upload a file");
      return;
    }

    const parsedLeads = parseCSV(csvData);
    if (parsedLeads.length === 0) {
      toast.error("No valid leads found in CSV");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/leads/import-bulk`, {
        leads: parsedLeads,
      });

      toast.success(`Imported ${response.data.imported} leads (${response.data.skipped} skipped as duplicates)`);
      setCsvData("");
      setImportDialogOpen(false);
      fetchPipelineStatus();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendToClay = async () => {
    if (!clayWebhookUrl.trim()) {
      toast.error("Please enter your Clay table webhook URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/clay/send-batch`, {
        batch_size: batchSize,
        clay_webhook_url: clayWebhookUrl,
      });

      toast.success(`Sent ${response.data.count} leads to Clay for enrichment`);
      fetchPipelineStatus();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send to Clay");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAnalyze = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/clay/auto-analyze?batch_size=10`);
      toast.success(response.data.message);
      fetchPipelineStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Auto-analyze failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      imported: { color: "bg-blue-100 text-blue-700", icon: Database },
      pending_enrichment: { color: "bg-yellow-100 text-yellow-700", icon: Clock },
      enriching: { color: "bg-purple-100 text-purple-700", icon: Loader2 },
      enriched: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      error: { color: "bg-red-100 text-red-700", icon: AlertCircle },
      new: { color: "bg-slate-100 text-slate-700", icon: Building2 },
    };
    const config = configs[status] || configs.new;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const sampleCSV = `company_name,website,linkedin_url,industry,employees,location
Stripe,stripe.com,https://linkedin.com/company/stripe,Fintech,5000+,San Francisco
Notion,notion.so,https://linkedin.com/company/notion,SaaS,500-1000,San Francisco
Figma,figma.com,https://linkedin.com/company/figma,Design Tools,1000-5000,San Francisco`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Clay Pipeline
              </h1>
              <p className="text-sm text-slate-500">
                Bulk import, enrich with Clay, auto-analyze
              </p>
            </div>
            <Button onClick={() => { fetchPipelineStatus(); fetchLeads(); }} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="p-6">
        {/* Pipeline Stats */}
        {pipelineStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Upload className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">
                    {pipelineStatus.enrichment_pipeline?.pending || 0}
                  </div>
                  <div className="text-xs text-slate-500">Pending Enrichment</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Loader2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">
                    {pipelineStatus.enrichment_pipeline?.enriching || 0}
                  </div>
                  <div className="text-xs text-slate-500">Enriching</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">
                    {pipelineStatus.enrichment_pipeline?.enriched || 0}
                  </div>
                  <div className="text-xs text-slate-500">Enriched</div>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">
                    {pipelineStatus.analysis_pipeline?.completed || 0}
                  </div>
                  <div className="text-xs text-slate-500">Analyzed</div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Pipeline Actions */}
        <Tabs defaultValue="import" className="mb-8">
          <TabsList className="mb-4">
            <TabsTrigger value="import" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Import Leads
            </TabsTrigger>
            <TabsTrigger value="enrich" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send to Clay
            </TabsTrigger>
            <TabsTrigger value="analyze" className="flex items-center gap-2">
              <Play className="w-4 h-4" />
              Auto-Analyze
            </TabsTrigger>
          </TabsList>

          <TabsContent value="import">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Bulk Import Leads</h3>
              <p className="text-sm text-slate-600 mb-4">
                Upload a CSV file or paste CSV data to import leads. Required columns: 
                <code className="mx-1 px-1 bg-slate-100 rounded">company_name</code> or 
                <code className="mx-1 px-1 bg-slate-100 rounded">website</code>
              </p>

              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="mr-4"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Upload CSV
                  </Button>
                  <span className="text-sm text-slate-500">or paste below</span>
                </div>

                <Textarea
                  placeholder={sampleCSV}
                  value={csvData}
                  onChange={(e) => setCsvData(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="csv-input"
                />

                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">
                    {csvData ? `${parseCSV(csvData).length} leads detected` : "Paste CSV data above"}
                  </span>
                  <Button onClick={handleImport} disabled={loading || !csvData.trim()}>
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Leads
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="enrich">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Send to Clay for Enrichment</h3>
              <p className="text-sm text-slate-600 mb-4">
                Send imported leads to your Clay table for enrichment. Clay will add company info,
                contacts, LinkedIn data, and more.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Clay Table Webhook URL
                  </label>
                  <Input
                    placeholder="https://api.clay.com/v1/tables/xxx/webhook"
                    value={clayWebhookUrl}
                    onChange={(e) => setClayWebhookUrl(e.target.value)}
                    data-testid="clay-webhook-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Find this in Clay: Table → Settings → Webhook URL
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Batch Size
                  </label>
                  <Input
                    type="number"
                    value={batchSize}
                    onChange={(e) => setBatchSize(parseInt(e.target.value) || 100)}
                    min={1}
                    max={1000}
                    className="w-32"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Max 100 for Starter plan (5,000 credits/query limit)
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-slate-500">
                    {pipelineStatus?.enrichment_pipeline?.pending || 0} leads ready for enrichment
                  </span>
                  <Button 
                    onClick={handleSendToClay} 
                    disabled={loading || !clayWebhookUrl.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send to Clay
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="analyze">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Auto-Analyze Enriched Leads</h3>
              <p className="text-sm text-slate-600 mb-4">
                Automatically run 360° GTM analysis on leads that have been enriched by Clay.
                This will crawl their websites and generate comprehensive reports.
              </p>

              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-emerald-600">
                      {pipelineStatus?.analysis_pipeline?.pending || 0}
                    </div>
                    <div className="text-sm text-slate-500">Pending Analysis</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">
                      {pipelineStatus?.analysis_pipeline?.completed || 0}
                    </div>
                    <div className="text-sm text-slate-500">Completed</div>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleAutoAnalyze} 
                disabled={loading || (pipelineStatus?.analysis_pipeline?.pending || 0) === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Start Auto-Analysis (10 leads)
                  </>
                )}
              </Button>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Leads Table */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Lead Pipeline</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No leads in pipeline. Import some to get started!
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">
                      {lead.company_name || lead.domain || "-"}
                    </TableCell>
                    <TableCell>
                      {lead.domain && (
                        <a
                          href={`https://${lead.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {lead.domain}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(lead.status)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">{lead.source || "-"}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "-"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {lead.analysis_job_id && (
                        <Link to={`/jobs/${lead.analysis_job_id}`}>
                          <Button variant="ghost" size="sm">
                            View Analysis
                          </Button>
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </main>
    </div>
  );
}
